import { TZDateMini } from '@date-fns/tz';
import { useState, useEffect, useMemo, useRef } from 'react';

import { getTimezone } from '@dynatrace-sdk/user-preferences';

/**
 * Custom hook that executes a callback function at specified intervals.
 * The first execution happens after `initialDelay`, and subsequent executions
 * happen every `repeatInterval` milliseconds.
 *
 * @param callback - The function to execute at each interval.
 * @param deps - Dependencies for updating the callback function.
 * @param initialDelay - Initial delay before the first execution in milliseconds.
 * @param repeatInterval - Interval between subsequent executions in milliseconds. If not provided it falls back to the initialDelay.
 * @returns An object containing the latest value returned by the callback and the last updated timestamp.
 * @internal
 */
export function useIntervalCallback<T>(
  callback: () => T,
  // oxlint-disable-next-line typescript/no-explicit-any
  deps: any[],
  initialDelay: number,
  repeatInterval?: number,
): { value: T | null; lastUpdated: number | null } {
  const [tick, setTick] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<number>(Date.now());

  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const intervalId = useRef<NodeJS.Timeout | null>(null);
  const timezone = getTimezone();

  const clearTimers = () => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    if (intervalId.current) {
      clearInterval(intervalId.current);
    }
  };

  const startInterval = () => {
    intervalId.current = setInterval(() => {
      setTick((t) => t + 1);
      setLastUpdated(TZDateMini.tz(timezone).getTime());
    }, repeatInterval ?? initialDelay);
  };

  const startTimeout = () => {
    timeoutId.current = setTimeout(() => {
      setTick((t) => t + 1);
      setLastUpdated(TZDateMini.tz(timezone).getTime());
      startInterval();
    }, initialDelay);
  };

  useEffect(() => {
    startTimeout();
    return clearTimers;
    // should only update on the dependencies provided.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps]);

  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const value = useMemo(callback, [tick, ...deps]);

  if (initialDelay <= 0 || (repeatInterval && repeatInterval <= 0)) {
    clearTimers();
    return { value: null, lastUpdated: null };
  }

  return { value, lastUpdated };
}
